import { FormControl } from '@angular/forms';

export interface BrandFormControls {
  businessName: FormControl<string>;
  businessCategory: FormControl<string>;
  businessType: FormControl<string>;
  regionOfOperation: FormControl<string>;
  businessRegistrationNumber: FormControl<string>;
  address: FormControl<string>;
  additionalAddressLine: FormControl<string>;
  state: FormControl<string>;
  city: FormControl<string>;
  zipCode: FormControl<string>;
  website: FormControl<string>;
  firstName: FormControl<string>;
  lastName: FormControl<string>;
  jobPosition: FormControl<string>;
  businessTitle: FormControl<string>;
  email: FormControl<string>;
  phoneNumber: FormControl<string>;
}

export interface CampaignFormControls {
  privacyPolicyUrl: FormControl<string>;
  termsAndConditionsUrl: FormControl<string>;
}

export interface CampaignFormControlsV2 {
  hasWebchatWidget: FormControl<boolean>;
  webchatWidgetUrl: FormControl<string>;
  hasWebForm: FormControl<boolean>;
  webFormUrl: FormControl<string>;
  privacyPolicyUrl: FormControl<string>;
  termsAndConditionsUrl: FormControl<string>;
}

export const BusinessType = {
  partnerShip: 'Partnership',
  limitedLiabilityCorporation: 'Limited Liability Corporation',
  coOperative: 'Co-operative',
  nonProfit: 'Non-profit Corporation',
  corporation: 'Corporation',
} as const;
export type BusinessType = (typeof BusinessType)[keyof typeof BusinessType];

// aka Industry
export const BusinessCategory = {
  agriculture: 'AGRICULTURE',
  automotiveIndustry: 'AUTOMOTIVE',
  banking: 'BANKING',
  construction: 'CONSTRUCTION',
  consumer: 'CONSUMER',
  education: 'EDUCATION',
  electronics: 'ELECTRONICS',
  energy: 'ENERGY',
  engineering: 'ENGINEERING',
  fastMovingConsumerGoods: 'FAST_MOVING_CONSUMER_GOODS',
  financial: 'FINANCIAL',
  fintech: 'FINTECH',
  foodAndBeverage: 'FOOD_AND_BEVERAGE',
  government: 'GOVERNMENT',
  healthcare: 'HEALTHCARE',
  hospitality: 'HOSPITALITY',
  insurance: 'INSURANCE',
  jewelry: 'JEWELRY',
  legal: 'LEGAL',
  manufacturing: 'MANUFACTURING',
  media: 'MEDIA',
  notForProfit: 'NOT_FOR_PROFIT',
  oilAndGas: 'OIL_AND_GAS',
  online: 'ONLINE',
  professionalServices: 'PROFESSIONAL_SERVICES',
  rawMaterials: 'RAW_MATERIALS',
  realEstate: 'REAL_ESTATE',
  religion: 'RELIGION',
  retail: 'RETAIL',
  technology: 'TECHNOLOGY',
  telecommunications: 'TELECOMMUNICATIONS',
  transportation: 'TRANSPORTATION',
  travel: 'TRAVEL',
} as const;
export type BusinessCategory = (typeof BusinessCategory)[keyof typeof BusinessCategory];

const businessCategoryLabels = new Map<string, string>([
  [BusinessCategory.agriculture, 'Agriculture'],
  [BusinessCategory.automotiveIndustry, 'Automotive Industry'],
  [BusinessCategory.banking, 'Banking'],
  [BusinessCategory.construction, 'Construction'],
  [BusinessCategory.consumer, 'Consumer'],
  [BusinessCategory.education, 'Education'],
  [BusinessCategory.electronics, 'Electronics'],
  [BusinessCategory.energy, 'Energy'],
  [BusinessCategory.engineering, 'Engineering'],
  [BusinessCategory.fastMovingConsumerGoods, 'Fast Moving Consumer Goods'],
  [BusinessCategory.financial, 'Financial'],
  [BusinessCategory.fintech, 'Fintech'],
  [BusinessCategory.foodAndBeverage, 'Food and Beverage'],
  [BusinessCategory.government, 'Government'],
  [BusinessCategory.healthcare, 'Healthcare'],
  [BusinessCategory.hospitality, 'Hospitality'],
  [BusinessCategory.insurance, 'Insurance'],
  [BusinessCategory.jewelry, 'Jewelry'],
  [BusinessCategory.legal, 'Legal'],
  [BusinessCategory.manufacturing, 'Manufacturing'],
  [BusinessCategory.media, 'Media'],
  [BusinessCategory.notForProfit, 'Not for Profit'],
  [BusinessCategory.oilAndGas, 'Oil and Gas'],
  [BusinessCategory.online, 'Online'],
  [BusinessCategory.professionalServices, 'Professional Services'],
  [BusinessCategory.rawMaterials, 'Raw Materials'],
  [BusinessCategory.realEstate, 'Real Estate'],
  [BusinessCategory.religion, 'Religion'],
  [BusinessCategory.retail, 'Retail'],
  [BusinessCategory.technology, 'Technology'],
  [BusinessCategory.telecommunications, 'Telecommunications'],
  [BusinessCategory.transportation, 'Transportation'],
  [BusinessCategory.travel, 'Travel'],
]);

export const businessCategoryToLabel = (businessCategory: string): string => {
  return businessCategoryLabels.get(businessCategory) ?? businessCategory;
};

export const Region = {
  africa: 'AFRICA',
  asia: 'ASIA',
  europe: 'EUROPE',
  latinAmerica: 'LATIN_AMERICA',
  usaAndCanada: 'USA_AND_CANADA',
} as const;
export type Region = (typeof Region)[keyof typeof Region];

const regionLabels = new Map<string, string>([
  [Region.africa, 'Africa'],
  [Region.asia, 'Asia'],
  [Region.europe, 'Europe'],
  [Region.latinAmerica, 'Latin America'],
  [Region.usaAndCanada, 'USA and Canada'],
]);

export const regionToLabel = (region: string): string => {
  return regionLabels.get(region) ?? region;
};

export const Position = {
  director: 'Director',
  gm: 'GM',
  vp: 'VP',
  ceo: 'CEO',
  cfo: 'CFO',
  generalCounsel: 'General Counsel',
  otherPosition: 'Other',
} as const;
export type Position = (typeof Position)[keyof typeof Position];
